import React, { useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Paper,
  Collapse,
  Box,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon   from '@mui/icons-material/KeyboardArrowUpRounded';
import WarningAmberRoundedIcon      from '@mui/icons-material/WarningAmberRounded';
import ExpandableRowPanel from './ExpandableRowPanel';
import {
  DANGER,
  WARNING,
  SUCCESS,
  INDIGO,
  TOOLBAR_BG_DARK,
  TOOLBAR_BG_LIGHT,
  SURFACE_LIGHT,
} from '../../utils/colors';

// ── Colour helpers ────────────────────────────────────────────────────────────

/** Returns cell sx overrides based on similarity (0-100). */
function similarityCellSx(pct) {
  if (pct > 70) {
    return {
      bgcolor: `${DANGER}18`,
      color: DANGER,
      fontWeight: 800,
    };
  }
  if (pct > 40) {
    return { color: WARNING, fontWeight: 700 };
  }
  return { color: SUCCESS, fontWeight: 600 };
}

/** Returns cell sx overrides based on AI risk score (0-100). */
function riskCellSx(score) {
  if (score > 60) {
    return {
      bgcolor: `${WARNING}18`,
      color: WARNING,
      fontWeight: 800,
    };
  }
  if (score > 80) {
    return {
      bgcolor: `${DANGER}18`,
      color: DANGER,
      fontWeight: 800,
    };
  }
  return { color: 'text.secondary', fontWeight: 500 };
}

function formatSeconds(sec) {
  if (sec == null) return '—';
  if (sec < 60)   return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Column config ─────────────────────────────────────────────────────────────

const COLUMNS = [
  { id: 'expand',              label: '',                       sortable: false, align: 'center', width: 42 },
  { id: 'studentA',           label: 'Student A',              sortable: false, align: 'left'            },
  { id: 'studentB',           label: 'Student B',              sortable: false, align: 'left'            },
  { id: 'batch',              label: 'Batch',                  sortable: false, align: 'left'            },
  { id: 'problemSlug',        label: 'Problem',                sortable: false, align: 'left'            },
  { id: 'winnowingSimilarity', label: 'Winnowing Similarity',  sortable: true,  align: 'center'          },
  { id: 'aiRiskScore',        label: 'AI Risk Score',          sortable: false, align: 'center'          },
  { id: 'submissionTimeSeconds', label: 'Submission Time',     sortable: false, align: 'center'          },
  { id: 'flagged',            label: 'Flagged',                sortable: false, align: 'center'          },
  { id: 'checkedAt',          label: 'Checked At',             sortable: true,  align: 'left'            },
];

// ── Expandable data row ───────────────────────────────────────────────────────

function DataRow({ row, isExpanded, onToggle }) {
  const simSx    = similarityCellSx(row.winnowingSimilarity ?? 0);
  const riskSx   = riskCellSx(row.aiRiskScore ?? 0);

  return (
    <>
      {/* Main row */}
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          '& > td': {
            borderBottom: isExpanded ? 'none' : undefined,
            transition: 'background 0.1s',
          },
        }}
        onClick={onToggle}
      >
        {/* Expand toggle */}
        <TableCell align="center" sx={{ py: 0.5, px: 0.5, width: 42 }} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" sx={{ color: INDIGO }}>
              {isExpanded
                ? <KeyboardArrowUpRoundedIcon   fontSize="small" />
                : <KeyboardArrowDownRoundedIcon fontSize="small" />
              }
            </IconButton>
          </Tooltip>
        </TableCell>

        {/* Student A */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
              {row.studentAName ?? row.studentA}
            </Typography>
            {row.studentARollNumber && (
              <Typography variant="caption" color="text.secondary" display="block">
                {row.studentARollNumber}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: INDIGO, opacity: 0.8 }}>
              @{row.studentA}
            </Typography>
          </Box>
        </TableCell>

        {/* Student B */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
              {row.studentBName ?? row.studentB}
            </Typography>
            {row.studentBRollNumber && (
              <Typography variant="caption" color="text.secondary" display="block">
                {row.studentBRollNumber}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: INDIGO, opacity: 0.8 }}>
              @{row.studentB}
            </Typography>
          </Box>
        </TableCell>

        {/* Batch */}
        <TableCell>
          {(row.studentABatchName || row.studentBBatchName) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              {row.studentABatchName && (
                <Chip
                  label={row.studentABatchName}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 600, bgcolor: `${INDIGO}18`, color: INDIGO, border: `1px solid ${INDIGO}40` }}
                />
              )}
              {row.studentBBatchName && row.studentBBatchName !== row.studentABatchName && (
                <Chip
                  label={row.studentBBatchName}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 600, bgcolor: `${INDIGO}18`, color: INDIGO, border: `1px solid ${INDIGO}40` }}
                />
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>

        {/* Problem */}
        <TableCell>
          <Box
            component="a"
            href={`https://leetcode.com/problems/${row.problemSlug}/`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: INDIGO,
              textDecoration: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {row.problemSlug}
          </Box>
        </TableCell>

        {/* Winnowing Similarity */}
        <TableCell align="center" sx={{ ...simSx, borderRadius: '0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {(row.winnowingSimilarity ?? 0) > 70 && (
              <WarningAmberRoundedIcon sx={{ fontSize: 14, color: DANGER }} />
            )}
            {row.winnowingSimilarity != null ? `${row.winnowingSimilarity}%` : '—'}
          </Box>
        </TableCell>

        {/* AI Risk Score */}
        <TableCell align="center" sx={riskSx}>
          {row.aiRiskScore != null ? `${row.aiRiskScore}` : '—'}
        </TableCell>

        {/* Submission Time */}
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {formatSeconds(row.submissionTimeSeconds)}
          </Typography>
        </TableCell>

        {/* Flagged */}
        <TableCell align="center">
          {row.flagged ? (
            <Chip
              label="Flagged"
              size="small"
              sx={{
                bgcolor: `${DANGER}18`,
                color: DANGER,
                fontWeight: 700,
                fontSize: '0.68rem',
              }}
            />
          ) : (
            <Chip
              label="Clean"
              size="small"
              sx={{
                bgcolor: `${SUCCESS}18`,
                color: SUCCESS,
                fontWeight: 700,
                fontSize: '0.68rem',
              }}
            />
          )}
        </TableCell>

        {/* Checked At */}
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {row.checkedAt ? new Date(row.checkedAt).toLocaleString() : '—'}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expandable detail row — spans all columns */}
      <TableRow>
        <TableCell colSpan={COLUMNS.length} sx={{ p: 0, border: 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandableRowPanel row={row} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * PlagiarismTable
 *
 * Props:
 *   rows        {array}   – result array from getWinnowingAnalytics
 *   total       {number}  – total record count (for server-side pagination)
 *   loading     {boolean}
 *   page        {number}  – 0-based MUI page
 *   rowsPerPage {number}
 *   sortBy      {string}
 *   sortDir     {string}  'asc' | 'desc'
 *   onPageChange      {(event, page) => void}
 *   onRowsPerPageChange {(event) => void}
 *   onSortChange      {(field) => void}
 */
export default function PlagiarismTable({
  rows = [],
  total = 0,
  loading = false,
  page = 0,
  rowsPerPage = 25,
  sortBy = 'checkedAt',
  sortDir = 'desc',
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) {
  const [expandedId, setExpandedId] = useState(null);

  const handleToggle = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const createSortHandler = (field) => () => onSortChange(field);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ borderRadius: 2, overflow: 'hidden' }}
    >
      <TableContainer
        sx={{
          maxHeight: 'calc(100vh - 280px)',
          overflowX: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          {/* ── Header ──────────────────────────────────────────────────── */}
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sortDirection={sortBy === col.id ? sortDir : false}
                  sx={{
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? '#1e1e2f' : TOOLBAR_BG_LIGHT,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? '#c9d1d9' : '#374151',
                    borderBottom: `2px solid ${INDIGO}40`,
                    width: col.width,
                    whiteSpace: 'nowrap',
                    px: col.id === 'expand' ? 0.5 : 1.5,
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDir : 'desc'}
                      onClick={createSortHandler(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ── Body ────────────────────────────────────────────────────── */}
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No plagiarism results found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.map((row) => {
              const id = String(row._id);
              return (
                <DataRow
                  key={id}
                  row={row}
                  isExpanded={expandedId === id}
                  onToggle={() => handleToggle(id)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? TOOLBAR_BG_DARK : SURFACE_LIGHT,
        }}
      />
    </Paper>
  );
}
